import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useScrollFrames } from "@/hooks/useScrollFrames";
import { ArrowRight, Play, Sparkles, Zap, MessagesSquare, Workflow, LineChart, ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

const ease = [0.22, 1, 0.36, 1];
const TOTAL_FRAMES = 143;
const FRAME_PATH = (i) => `/media/frames/f_${String(i).padStart(3, "0")}.jpg`;

export default function Hero({ onOpenLead }) {
  const { t } = useLang();
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // 3 ACTS — brain animation never has text on top.
  // 0.00 – 0.14 → INTRO     (eyebrow + title at the top)
  // 0.14 – 0.82 → BRAIN ALONE (pure animation)
  // 0.82 – 0.94 → BRAIN FADES OUT into a clean dark canvas
  // 0.94 – 1.00 → OUTRO      (CTAs on clean dark space, no overlap)
  const introOpacity = useTransform(scrollYProgress, [0, 0.14, 0.22], [1, 1, 0]);
  const introY = useTransform(scrollYProgress, [0, 0.22], [0, -40]);

  // Brain fades OUT before the outro shows up so text never sits on top of it
  const brainOpacity = useTransform(scrollYProgress, [0, 0.05, 0.82, 0.94], [0.6, 1, 1, 0]);
  // Full-screen dim that takes over once the brain finishes
  const outroDim = useTransform(scrollYProgress, [0.82, 0.94, 1], [0, 0.92, 0.98]);

  const outroOpacity = useTransform(scrollYProgress, [0.9, 0.96, 1], [0, 1, 1]);
  const outroY = useTransform(scrollYProgress, [0.9, 1], [40, 0]);

  // Dark veil — obscures the brain at scroll 0, fades out as user scrolls
  const titleVeilOpacity = useTransform(scrollYProgress, [0, 0.06, 0.22, 0.34], [1, 0.95, 0.4, 0]);

  // Scroll hint visibility
  const hintOpacity = useTransform(scrollYProgress, [0, 0.08], [1, 0]);

  // Frame-by-frame canvas scrubbing tied to scroll (see hooks/useScrollFrames)
  const canvasRef = useScrollFrames(scrollYProgress, TOTAL_FRAMES, FRAME_PATH, { fitMode: "contain", scale: 1, verticalAnchor: 0.5 });

  return (
    <section
      ref={containerRef}
      id="top"
      data-testid="hero-section"
      className="relative w-full bg-black"
      style={{ height: "320vh" }}
    >
      <div className="sticky top-0 h-[100svh] w-full overflow-hidden">
        {/* === BRAIN CANVAS — fades out cleanly before OUTRO starts === */}
        <motion.div
          style={{ opacity: brainOpacity }}
          className="absolute inset-0 z-0"
        >
          <canvas
            ref={canvasRef}
            data-testid="hero-canvas"
            className="w-full h-full block"
          />
        </motion.div>

        {/* Full-screen dim that takes over once the brain animation ends */}
        <motion.div
          style={{ opacity: outroDim }}
          className="absolute inset-0 z-[1] pointer-events-none bg-black"
        />

        {/* === ACT 1 — INTRO at TOP (above brain, never over it) === */}

        {/* Scroll-reveal dark veil — obscures the brain at scroll 0, fades out as user scrolls */}
        <motion.div
          style={{ opacity: titleVeilOpacity }}
          className="absolute inset-0 z-[2] pointer-events-none"
          aria-hidden="true"
        >
          <div className="absolute inset-x-0 top-0 h-[58%] bg-[#020409]" />
          <div className="absolute inset-x-0 top-[58%] h-[28%] bg-gradient-to-b from-[#020409] via-[#020409]/85 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-[70%] backdrop-blur-[4px]" />
        </motion.div>

        <motion.div
          style={{ opacity: introOpacity, y: introY }}
          className="absolute top-0 left-0 right-0 z-10 pt-28 sm:pt-32 pb-8"
        >
          <div className="mx-auto max-w-7xl px-5">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease, delay: 0.15 }}
              className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 mb-5"
              data-testid="hero-eyebrow"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brand-blue opacity-70 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-blue" />
              </span>
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/80 font-mono-tight">
                {t.hero.eyebrow}
              </span>
            </motion.div>

            <h1 className="font-display text-[40px] leading-[1.04] sm:text-5xl md:text-[58px] lg:text-[68px] lg:leading-[1.0] font-black tracking-tighter text-white max-w-3xl [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]">
              <span className="block overflow-hidden">
                <RevealWord delay={0.2}>
                  {t.hero.title1.split(" ").slice(0, -1).join(" ")}{" "}
                  <span className="text-brand-orange">{t.hero.title1.split(" ").slice(-1)[0]}</span>
                </RevealWord>
              </span>
              <span className="block overflow-hidden">
                <RevealWord delay={0.32}>
                  {t.hero.title2}{" "}
                  <span className="text-gradient-brand">{t.hero.title3}</span>
                </RevealWord>
              </span>
            </h1>
          </div>
        </motion.div>

        {/* === ACT 3 — OUTRO on a clean dark canvas (brain has faded out) === */}
        <motion.div
          style={{ opacity: outroOpacity, y: outroY }}
          className="absolute inset-0 z-10 flex items-center"
        >
          <div className="mx-auto max-w-7xl px-5 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_auto] gap-10 lg:gap-14 items-center">
              <div>
                <div className="text-[10.5px] sm:text-[11.5px] uppercase tracking-[0.28em] text-brand-orange font-mono-tight mb-3">
                  {t.hero.act3_eyebrow}
                </div>
                <h3 className="font-display text-3xl sm:text-5xl md:text-[48px] lg:text-[56px] font-black tracking-tighter text-white leading-[1.02] max-w-2xl">
                  {t.hero.act3_title1}
                  <span className="text-gradient-blue"> {t.hero.act3_title2}</span>.
                </h3>
                <p className="mt-5 text-white/65 max-w-xl text-[15px] sm:text-base lg:text-lg leading-relaxed">
                  {t.hero.subtitle}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <button
                    onClick={onOpenLead}
                    data-testid="hero-cta-primary"
                    className="group inline-flex items-center gap-2 px-6 py-4 rounded-full text-sm font-semibold text-white btn-primary-glow"
                  >
                    {t.hero.cta_primary}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                  <a
                    href="#portfolio"
                    data-testid="hero-cta-secondary"
                    className="group inline-flex items-center gap-2 px-6 py-4 rounded-full text-sm font-semibold text-white btn-ghost-glass"
                  >
                    <Play className="h-3.5 w-3.5 fill-white" />
                    {t.hero.cta_secondary}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-60 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>

                <div className="mt-10 grid grid-cols-3 gap-5 max-w-xl" data-testid="hero-stats">
                  {[
                    { v: t.hero.stat1, l: t.hero.stat1_label },
                    { v: t.hero.stat2, l: t.hero.stat2_label },
                    { v: t.hero.stat3, l: t.hero.stat3_label },
                  ].map((s) => (
                    <div key={s.l} className="border-l border-white/15 pl-3">
                      <div className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">
                        {s.v}
                      </div>
                      <div className="text-[11px] sm:text-xs text-white/55 leading-snug mt-1">
                        {s.l}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Side badges — clean column, no overlap */}
              <div className="hidden lg:flex flex-col gap-2.5">
                <SideBadge
                  testId="badge-1"
                  icon={<Zap className="h-3.5 w-3.5 text-brand-orange" />}
                  title={t.hero.badges.b1}
                  sub={t.hero.badges.b1_sub}
                  tone="orange"
                />
                <SideBadge
                  testId="badge-2"
                  icon={<Sparkles className="h-3.5 w-3.5 text-brand-blue" />}
                  title={t.hero.badges.b2}
                  sub={t.hero.badges.b2_sub}
                  tone="blue"
                />
                <SideBadge
                  testId="badge-3"
                  icon={<LineChart className="h-3.5 w-3.5 text-brand-blue" />}
                  title={t.hero.badges.b3}
                  sub={t.hero.badges.b3_sub}
                  tone="blue"
                />
                <SideBadge
                  testId="badge-4"
                  icon={<MessagesSquare className="h-3.5 w-3.5 text-brand-orange" />}
                  title={t.hero.badges.b4}
                  sub={t.hero.badges.b4_sub}
                  tone="orange"
                />
                <SideBadge
                  testId="badge-5"
                  icon={<Workflow className="h-3.5 w-3.5 text-white" />}
                  title={t.hero.badges.b5}
                  sub={t.hero.badges.b5_sub}
                  tone="neutral"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Scroll hint pill — only shown at the very start */}
        <motion.div
          style={{ opacity: hintOpacity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
          data-testid="hero-scroll-hint"
        >
          <div className="glass-strong rounded-full pl-3 pr-2 py-1.5 flex items-center gap-2.5 ring-1 ring-white/10 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]">
            <span className="text-[10px] uppercase tracking-[0.32em] text-white/85 font-mono-tight">
              {t.hero.scroll_hint}
            </span>
            <div className="h-7 w-[16px] rounded-full border border-white/20 grid place-items-start p-1">
              <motion.span
                className="block w-[2px] h-[5px] rounded-full bg-white/80"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </div>
        </motion.div>

        {/* Bottom gradient — blends hero into the next section */}
        <div className="absolute inset-x-0 bottom-0 h-24 z-[2] pointer-events-none bg-gradient-to-b from-transparent to-brand-ink" />
      </div>
    </section>
  );
}

function RevealWord({ children, delay = 0, className = "" }) {
  return (
    <motion.span
      initial={{ y: "110%", opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 0.95, ease, delay }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.span>
  );
}

const TONE_SHADOWS = {
  orange: "shadow-[0_0_30px_-12px_rgba(255,122,24,0.55)]",
  neutral: "shadow-[0_0_30px_-12px_rgba(255,255,255,0.15)]",
  blue: "shadow-[0_0_30px_-12px_rgba(23,105,255,0.6)]",
};

function SideBadge({ icon, title, sub, tone = "blue", testId }) {
  const toneStyle = TONE_SHADOWS[tone] ?? TONE_SHADOWS.blue;
  return (
    <motion.div
      initial={{ opacity: 0, x: 18 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      whileHover={{ x: -2, scale: 1.02 }}
      data-testid={testId}
      className={`glass-strong rounded-xl p-2.5 min-w-[200px] ${toneStyle}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="h-7 w-7 grid place-items-center rounded-lg bg-white/5 border border-white/10">
          {icon}
        </div>
        <div className="flex-1">
          <div className="text-[12px] lg:text-[13px] font-semibold text-white leading-none tracking-tight">{title}</div>
          <div className="text-[10px] text-white/55 mt-1 font-mono-tight uppercase tracking-wider">{sub}</div>
        </div>
        <div className="relative flex h-2 w-2">
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${tone === 'orange' ? 'bg-brand-orange' : 'bg-brand-blue'}`} />
          <span className={`relative inline-flex h-2 w-2 rounded-full ${tone === 'orange' ? 'bg-brand-orange' : 'bg-brand-blue'}`} />
        </div>
      </div>
    </motion.div>
  );
}
